import { defineStore } from "pinia";
import { ref, computed } from "vue";
import useMyFetch from "@stores/useMyFetch";
import handleError from "@utils/composables/handleError";
import { buildHubSpotProperties } from "./pricerHubSpotMapping";
import { usePricerProjectStore } from "./pricerProject";

export const usePricerHubSpotStore = defineStore("pricerHubSpot", () => {
  const hubSpotData = ref({});
  const companies = ref([]);
  const contacts = ref([]);

  const isLoading = ref(false);
  const isSyncing = ref(false);
  const isLoadingCompanies = ref(false);
  const isLoadingContacts = ref(false);

  const companiesOptions = computed(() =>
    companies.value.map((c) => ({
      value: c.id,
      label: c.name,
    })),
  );

  const contactsOptions = computed(() =>
    contacts.value.map((c) => ({
      value: c.id,
      label: `${c.firstname || ""} ${c.lastname || ""}`.trim(),
    })),
  );

  const getCompanyById = (companyId) => companies.value.find((c) => c.id === companyId);
  const getContactById = (contactId) => contacts.value.find((c) => c.id === contactId);

  const getContactFullName = (contact) => {
    if (!contact) return "";
    return `${contact.firstname || ""} ${contact.lastname || ""}`.trim();
  };

  const getHubSpotData = () => {
    return { ...hubSpotData.value };
  };

  const fetchHubSpotData = async (projectToken) => {
    if (!projectToken) {
      isLoading.value = false;
      return getHubSpotData();
    }

    isLoading.value = true;

    try {
      const { response, data } = await useMyFetch(`/pricer/projects/${projectToken}/hubspot/deal`).json();

      if (response.value.ok && data.value?.hubspot_deal) {
        const deal = data.value.hubspot_deal;
        hubSpotData.value = deal;
      }
    } catch (error) {
      handleError(error);
      hubSpotData.value = {};
    } finally {
      isLoading.value = false;
    }

    return getHubSpotData();
  };

  const syncToHubSpot = async (projectData, changedFields) => {
    if (!projectData?.token) {
      console.error("No project token provided for HubSpot sync");
      return { success: false, error: "No project token" };
    }

    isSyncing.value = true;
    try {
      const properties = buildHubSpotProperties(changedFields);

      if (Object.keys(properties).length === 0) {
        return { success: false, error: "No fields to update" };
      }

      const hasCrmId = projectData.crm_id && String(projectData.crm_id).trim() !== "";
      const fetchMethod = hasCrmId ? "patch" : "post";

      const { response, data } = await useMyFetch(`/pricer/projects/${projectData.token}/hubspot/deal`)
        .json()
        [fetchMethod]({ properties });

      if (response.value.ok && data.value?.success) {
        // If a new deal was created, update project with the new crm_id and save
        if (!hasCrmId && data.value?.crm_id) {
          const projectStore = usePricerProjectStore();
          projectStore.project.crm_id = data.value.crm_id;
          await projectStore.saveProject();
        }

        await fetchHubSpotData(projectData.token);
        return { success: true, message: data.value.message };
      }
    } catch (error) {
      handleError(error);
      return { success: false, error: error.message || "Failed to sync with HubSpot" };
    } finally {
      isSyncing.value = false;
    }
  };

  const fetchCompanies = async (query = "") => {
    isLoadingCompanies.value = true;
    try {
      const url = query
        ? `/pricer/hubspot/companies?query=${encodeURIComponent(query)}`
        : `/pricer/hubspot/companies`;

      const { response, data } = await useMyFetch(url).json();

      if (response.value.ok && data.value?.companies) {
        companies.value = data.value.companies;
      }
    } catch (error) {
      handleError(error);
      companies.value = [];
    } finally {
      ensureProjectSelectionInList();
      isLoadingCompanies.value = false;
    }
  };

  const searchCompanies = async (query) => {
    await fetchCompanies(query);
  };

  const fetchContacts = async (companyId, query = "") => {
    if (!companyId) return;

    isLoadingContacts.value = true;
    try {
      const body = { company_id: companyId };
      if (query) body.query = query;

      const { response, data } = await useMyFetch(`/pricer/hubspot/company_contacts`).json().post(body);

      if (response.value.ok && data.value?.contacts) {
        contacts.value = data.value.contacts;
      }
    } catch (error) {
      handleError(error);
      contacts.value = [];
    } finally {
      ensureProjectSelectionInList();
      isLoadingContacts.value = false;
    }
  };

  const searchContacts = async (companyId, query) => {
    await fetchContacts(companyId, query);
  };

  const clearContacts = () => {
    contacts.value = [];
  };

  // Ensure the project's selected company/contact are always present in the lists
  const ensureProjectSelectionInList = () => {
    const { project } = usePricerProjectStore();

    if (project.company_id && project.client) {
      if (!companies.value.find((c) => c.id === project.company_id)) {
        companies.value = [{ id: project.company_id, name: project.client }, ...companies.value];
      }
    }

    if (project.contact_id && project.contact_name) {
      if (!contacts.value.find((c) => c.id === project.contact_id)) {
        const [firstname = "", ...rest] = project.contact_name.split(" ");
        const lastname = rest.join(" ");
        contacts.value = [{ id: project.contact_id, firstname, lastname }, ...contacts.value];
      }
    }
  };

  return {
    hubSpotData,
    companies,
    contacts,
    companiesOptions,
    contactsOptions,
    isLoading,
    isSyncing,
    isLoadingCompanies,
    isLoadingContacts,
    getHubSpotData,
    getCompanyById,
    getContactById,
    getContactFullName,
    fetchHubSpotData,
    fetchCompanies,
    fetchContacts,
    searchCompanies,
    searchContacts,
    clearContacts,
    ensureProjectSelectionInList,
    syncToHubSpot,
  };
});
